import { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '../../../components/ui';
import { IMAGE_CONSTRAINTS } from '../../../types/knowledgeBase';
import './ImageUpload.css';

interface ImageUploadProps {
  imageUrl?: string;
  imageAlt?: string;
  onChange: (imageUrl: string | undefined) => void;
  onAltChange: (alt: string | undefined) => void;
}

/**
 * Resizes an image to fit within max dimensions while maintaining aspect ratio
 */
async function resizeImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const { MAX_WIDTH, MAX_HEIGHT, QUALITY } = IMAGE_CONSTRAINTS;

        // Calculate new dimensions
        if (width > MAX_WIDTH || height > MAX_HEIGHT) {
          const ratio = Math.min(MAX_WIDTH / width, MAX_HEIGHT / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }

        // Create canvas and draw resized image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64
        const dataUrl = canvas.toDataURL('image/jpeg', QUALITY);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export function ImageUpload({ imageUrl, imageAlt, onChange, onAltChange }: ImageUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const processFile = useCallback(
    async (file: File) => {
      setError(null);

      // Validate file type
      const acceptedTypes: readonly string[] = IMAGE_CONSTRAINTS.ACCEPTED_TYPES;
      if (!acceptedTypes.includes(file.type)) {
        setError('Please upload a JPEG, PNG, GIF, or WebP image');
        return;
      }

      // Validate initial file size (before compression)
      if (file.size > 10 * 1024 * 1024) {
        // 10MB max initial size
        setError('Image is too large. Please use an image under 10MB');
        return;
      }

      setIsProcessing(true);
      try {
        const resizedDataUrl = await resizeImage(file);

        // Check final size
        const base64Length = resizedDataUrl.length - 'data:image/jpeg;base64,'.length;
        const finalSize = (base64Length * 3) / 4; // Approximate byte size

        if (finalSize > IMAGE_CONSTRAINTS.MAX_SIZE_BYTES) {
          setError('Image is still too large after compression. Please use a smaller image');
          return;
        }

        onChange(resizedDataUrl);
      } catch {
        setError('Failed to process image');
      } finally {
        setIsProcessing(false);
      }
    },
    [onChange]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        processFile(file);
      }
      // Reset input to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [processFile]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        processFile(file);
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleRemove = useCallback(() => {
    onChange(undefined);
    onAltChange(undefined);
    setError(null);
  }, [onChange, onAltChange]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  if (imageUrl) {
    return (
      <Box className="image-upload image-upload--has-image">
        <Box className="image-upload__preview">
          <img src={imageUrl} alt={imageAlt ?? 'Step illustration'} />
        </Box>
        <Box className="image-upload__actions">
          <input
            type="text"
            className="image-upload__alt-input"
            value={imageAlt ?? ''}
            onChange={(e) => onAltChange(e.target.value)}
            placeholder="Image description (for accessibility)"
          />
          <button
            className="image-upload__remove"
            onClick={handleRemove}
            type="button"
            aria-label="Remove image"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            Remove
          </button>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="image-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_CONSTRAINTS.ACCEPTED_TYPES.join(',')}
        onChange={handleFileSelect}
        className="image-upload__input"
        aria-label="Upload image"
      />
      <Box
        className={`image-upload__dropzone ${isDragging ? 'image-upload__dropzone--dragging' : ''} ${isProcessing ? 'image-upload__dropzone--processing' : ''}`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
      >
        {isProcessing ? (
          <Typography variant="body2" className="image-upload__processing">
            Processing image...
          </Typography>
        ) : (
          <>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="image-upload__icon"
            >
              <rect
                x="3"
                y="5"
                width="18"
                height="14"
                rx="2"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor" />
              <path
                d="M3 15l4.5-4.5a1 1 0 011.4 0l3.1 3.1a1 1 0 001.4 0l1.1-1.1a1 1 0 011.4 0L21 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Typography variant="body2" className="image-upload__text">
              <span className="image-upload__link">Choose file</span> or drag and drop
            </Typography>
            <Typography variant="caption" className="image-upload__hint">
              JPEG, PNG, GIF, or WebP (max 10MB)
            </Typography>
          </>
        )}
      </Box>
      {error && <Typography className="image-upload__error">{error}</Typography>}
    </Box>
  );
}
